import React, { useEffect, useRef, useState, startTransition } from "react";
import PDFViewer from "../Viewers/PDFViewer";
import PanoramaViewer from "../Viewers/PanoramaViewer";
import { getTypeIcon } from "./getTypeIcon";
import { useLanguage } from "../LanguageContext";
import { ContentRendererProps, Link } from "../../Types/types";
import CircularProgress from "@mui/material/CircularProgress";
import ModelViewer from "../Viewers/ModelViever";
import { placeholderImages } from "../../utils/placeholderImages";
import { getBaseUrl } from "../Manual_List/fetch/baseEndpoint";
import ZoomableImage from "./ZoomableImage";

const ContentRenderer: React.FC<ContentRendererProps> = ({
  step,
  contentUrl,
  isDrawerOpen,
  handleThumbnailClick,
  showThumbnail,
  pdfWidth,
  updateContentUrl,
  fileSize,
  setIsFullscreen,
  handleStepUpdate,
  setLoading,
}) => {
  const { language } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isContentLoading, setIsContentLoading] = useState<boolean>(true);
  const [isIosSrcLoading, setIsIosSrcLoading] = useState<boolean>(true);
  const [iosSrc, setIosSrc] = useState<string | null>(null);
  const [stepId, setStepId] = useState<number>(step.id);
  const [prefetchedContent, setPrefetchedContent] = useState<{
    [key: number]: string;
  }>({});

  const rotationY = step.settings
    ? JSON.parse(step.settings).rotationY
    : undefined;

  useEffect(() => {
    setError(null);
    setIsPlaying(false);
    setIsContentLoading(true);
    setLoading(false);
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [contentUrl, setLoading]);

  useEffect(() => {
    setStepId(step.id);

    const fetchIosSrc = async () => {
      const token = localStorage.getItem("token");
      setIsIosSrcLoading(true);
      try {
        const response = await fetch(
          `${getBaseUrl()}/packageContent/${step.id}/content?platform=ios`,
          {
            headers: {
              Accept: "application/zip",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(
            new Blob([blob], { type: "model/vnd.usdz+zip" })
          );
          setIosSrc(url);
        } else {
          setIosSrc(null);
        }
      } catch (error) {
        console.error("Failed to fetch iosSrc", error);
        setIosSrc(null);
      } finally {
        setIsIosSrcLoading(false);
      }
    };

    if (step.type === "object") {
      fetchIosSrc();
    }
  }, [step.type, step.id]);

  useEffect(() => {
    const prefetchLinkedContent = async () => {
      if (step.links) {
        for (const link of step.links) {
          const nextId = link.targetPackageContentId;
          const nextContent = await fetchContentById(nextId);
          if (nextContent) {
            setPrefetchedContent((prev) => ({
              ...prev,
              [nextId]: nextContent.url,
            }));
          }
        }
      }
    };

    prefetchLinkedContent();
  }, [step.links]);

  const handleVideoLoaded = () => {
    setIsContentLoading(false);
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleThumbnailClickInternal = () => {
    handleThumbnailClick();
  };

  const fetchContentById = async (id: number) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `${getBaseUrl()}/packageContent/${id}/content`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        return { url };
      } else {
        console.error(`Could not load ${id}: ${response.statusText}`);
        return null;
      }
    } catch (error) {
      console.error(`Could not load ${id}: ${error}`);
      return null;
    }
  };

  if (!step) return <div>Select a step</div>;

  if (showThumbnail) {
    const cleanedThumbnail =
      step.thumbnail ||
      (placeholderImages as { [key: string]: string })[step.type];
    return (
      <div className="relative flex flex-col justify-center items-center h-full">
        <div className="sm:w-1/3 w-auto">
          <img
            src={cleanedThumbnail}
            className="max-w-full max-h-screen rounded-md object-contain cursor-pointer"
            onClick={handleThumbnailClickInternal}
          />
          <div className="mt-2 text-left flex flex-row items-left justify-between bg-white p-2 rounded-md shadow-md w-full">
            <div className="flex flex-row items-center">
              {getTypeIcon(step.type)}{" "}
              <span className="ml-2">{step.label}</span>
            </div>
            {fileSize && (
              <div className="ml-auto text-gray-500">{fileSize}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!contentUrl) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircularProgress />
      </div>
    );
  }

  if (step.type === "video") {
    return (
      <div
        className={`max-w-full max-h-screen sm:w-2/3 w-auto object-contain ${
          isDrawerOpen ? "ml-10 mr-auto" : "mx-auto"
        }`}
      >
        {isContentLoading && (
          <div className="flex justify-center items-center h-full">
            <CircularProgress />
          </div>
        )}
        <video
          ref={videoRef}
          controls
          width="100%"
          height="100%"
          onPlay={handlePlay}
          onLoadedData={handleVideoLoaded}
          playsInline
          style={{ display: isContentLoading ? "none" : "block" }}
        >
          <source src={contentUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {error && <div className="text-red-500 mt-2">{error}</div>}
      </div>
    );
  }

  switch (step.type) {
    case "image":
      return <ZoomableImage src={contentUrl} alt={contentUrl} />;

    case "pdf":
      return (
        <div
          className={`mx-auto ${
            pdfWidth ? `w-${pdfWidth}` : "max-w-full"
          } max-h-screen`}
        >
          <PDFViewer file={contentUrl} numPages={step.numPages} />
        </div>
      );

    case "object":
      return (
        <ModelViewer
          modelPath={contentUrl}
          rotationY={parseFloat(rotationY || "0")}
          iosSrc={iosSrc}
        />
      );

    case "panorama":
      const clickToPaths = step.links.map((link: Link) => ({
        path: link.targetPackageContentId,
        angle: parseFloat(link.settings),
      }));

      return (
        <PanoramaViewer
          key={contentUrl}
          initialImagePath={contentUrl}
          clickToPaths={clickToPaths}
          onThumbnailClick={async (nextImagePath: string) => {
            const nextContent = await fetchContentById(parseInt(nextImagePath));
            if (nextContent) {
              startTransition(() => {
                updateContentUrl(nextContent.url);
                setStepId(parseInt(nextImagePath));
                handleStepUpdate(parseInt(nextImagePath));
              });
            }
          }}
          setIsFullscreen={setIsFullscreen}
          stepId={stepId}
          prefetchedContent={prefetchedContent}
          handleStepUpdate={handleStepUpdate}
        />
      );

    default:
      return <div>Unsupported type</div>;
  }
};

export default ContentRenderer;
